/**
 * Shared filter bar for Mystery Shopping surfaces (/reports + MS dashboard).
 * Extracted verbatim from ReportsList.js so both pages honour identical
 * filter semantics and testids. No visual or behavioural drift permitted.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Check, X, Download } from 'lucide-react';

export function toISODate(d) {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
export function presetRange(kind) {
  // Step 2 (Feb 2026) — 'all_time' is the new default. Returns an empty
  // range so no date filter is sent to the API, and every published report
  // in scope is visible immediately.
  if (kind === 'all_time') {
    return { from: '', to: '' };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (kind === 'today') {
    return { from: toISODate(today), to: toISODate(today) };
  }
  if (kind === 'this_month') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toISODate(first), to: toISODate(today) };
  }
  if (kind === 'last_month') {
    const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstLast = new Date(firstThis);
    firstLast.setMonth(firstLast.getMonth() - 1);
    const lastLast = new Date(firstThis);
    lastLast.setDate(lastLast.getDate() - 1);
    return { from: toISODate(firstLast), to: toISODate(lastLast) };
  }
  return { from: '', to: '' };
}
export function rangeEquals(a, b) {
  return (a?.from || '') === (b?.from || '') && (a?.to || '') === (b?.to || '');
}

export function FiltersBar({
  range, onRangeChange,
  branchType, onBranchTypeChange,
  selectedBranchIds, onSelectedBranchIdsChange,
  branches,
  onExport, exporting,
  showExport = true,
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);

  const activePreset = useMemo(() => {
    if (rangeEquals(range, presetRange('all_time'))) return 'all_time';
    if (rangeEquals(range, presetRange('today'))) return 'today';
    if (rangeEquals(range, presetRange('this_month'))) return 'this_month';
    if (rangeEquals(range, presetRange('last_month'))) return 'last_month';
    return 'custom';
  }, [range]);

  const clientBranches = branches.filter(b => b.branch_type === 'client');
  const competitorBranches = branches.filter(b => b.branch_type === 'competitor');

  const toggleBranch = (id) => {
    const set = new Set(selectedBranchIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    onSelectedBranchIdsChange(Array.from(set));
  };
  const clearBranches = () => onSelectedBranchIdsChange([]);
  const presetClick = (kind) => {
    if (kind === 'custom') { setCustomOpen(true); return; }
    setCustomOpen(false);
    onRangeChange(presetRange(kind));
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid="reports-filters">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap max-w-full rounded-lg bg-[#F1F5F9] p-0.5" data-testid="reports-date-presets">
          {[
            { key: 'all_time',   label: 'All Time' },
            { key: 'today',      label: 'Today' },
            { key: 'this_month', label: 'This Month' },
            { key: 'last_month', label: 'Last Month' },
            { key: 'custom',     label: 'Custom range' },
          ].map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => presetClick(p.key)}
              data-testid={`reports-preset-${p.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                activePreset === p.key
                  ? 'bg-white text-[#0F172A] shadow-sm'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >{p.label}</button>
          ))}
        </div>

        <div className="inline-flex flex-wrap max-w-full rounded-lg bg-[#F1F5F9] p-0.5" data-testid="reports-branch-type">
          {[
            { key: 'all',        label: 'All' },
            { key: 'client',     label: 'Client' },
            { key: 'competitor', label: 'Competitor' },
            { key: 'comparison', label: 'Comparison' },
          ].map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => onBranchTypeChange(p.key)}
              data-testid={`reports-branch-type-${p.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                branchType === p.key
                  ? 'bg-white text-[#0F172A] shadow-sm'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >{p.label}</button>
          ))}
        </div>

        <Popover open={branchPickerOpen} onOpenChange={setBranchPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="reports-filter-branch">
              {selectedBranchIds.length === 0
                ? 'Filter by branch'
                : `${selectedBranchIds.length} branch${selectedBranchIds.length === 1 ? '' : 'es'} selected`}
              <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-0" data-testid="reports-filter-branch-pop">
            <div className="max-h-[380px] overflow-auto py-1">
              {clientBranches.length === 0 && competitorBranches.length === 0 && (
                <p className="px-3 py-4 text-xs text-[#94A3B8]">No branches available.</p>
              )}
              {clientBranches.length > 0 && (
                <div>
                  <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">Your branches</p>
                  {clientBranches.map(b => {
                    const checked = selectedBranchIds.includes(b.branch_id);
                    return (
                      <div
                        key={b.branch_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleBranch(b.branch_id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBranch(b.branch_id); } }}
                        data-testid={`reports-filter-branch-opt-${b.branch_id}`}
                        className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-[#F8FAFC] cursor-pointer focus:outline-none focus-visible:bg-[#F8FAFC]"
                      >
                        <span className="flex items-center gap-2 text-[#0F172A]">
                          <span aria-hidden="true" className={`inline-flex items-center justify-center w-4 h-4 rounded border ${checked ? 'bg-[#1A3E6F] border-[#1A3E6F]' : 'border-[#CBD5E1] bg-white'}`}>
                            {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </span>
                          <span className="truncate">{b.name || b.branch_id}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {competitorBranches.length > 0 && (
                <div className="mt-1 border-t border-[#E2E8F0] pt-1">
                  <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">Competitor branches</p>
                  {competitorBranches.map(b => {
                    const checked = selectedBranchIds.includes(b.branch_id);
                    return (
                      <div
                        key={b.branch_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleBranch(b.branch_id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBranch(b.branch_id); } }}
                        data-testid={`reports-filter-branch-opt-${b.branch_id}`}
                        className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-[#F8FAFC] cursor-pointer focus:outline-none focus-visible:bg-[#F8FAFC]"
                      >
                        <span className="flex items-center gap-2 text-[#0F172A]">
                          <span aria-hidden="true" className={`inline-flex items-center justify-center w-4 h-4 rounded border ${checked ? 'bg-[#1A3E6F] border-[#1A3E6F]' : 'border-[#CBD5E1] bg-white'}`}>
                            {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </span>
                          <span className="truncate">{b.name || b.branch_id}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {selectedBranchIds.length > 0 && (
              <div className="border-t border-[#E2E8F0] p-2 flex justify-between items-center">
                <button
                  type="button"
                  onClick={clearBranches}
                  data-testid="reports-filter-branch-clear"
                  className="text-xs text-[#1A3E6F] hover:underline inline-flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear selection
                </button>
                <Badge variant="outline" className="text-[10px]">{selectedBranchIds.length} selected</Badge>
              </div>
            )}
          </PopoverContent>
        </Popover>

        <div className="flex-1" />

        {showExport && (
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={exporting}
            data-testid="reports-export-btn"
            className="h-9"
          >
            <Download className={`w-4 h-4 mr-1.5 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        )}
      </div>

      {(customOpen || activePreset === 'custom') && (
        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[#F1F5F9]" data-testid="reports-custom-range">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">From</label>
            <Input
              type="date"
              value={range.from}
              onChange={(e) => onRangeChange({ from: e.target.value, to: range.to })}
              className="h-9 w-[160px]"
              data-testid="reports-filter-date-from"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">To</label>
            <Input
              type="date"
              value={range.to}
              onChange={(e) => onRangeChange({ from: range.from, to: e.target.value })}
              className="h-9 w-[160px]"
              data-testid="reports-filter-date-to"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default FiltersBar;
